import React, { useState, useEffect, useRef } from 'react';
import { generatePatientDossierPdf } from '../utils/pdfGenerator';
import { createLabOrderCode } from '../utils/identifiers';

const PatientDetails = ({ patient, onBack, currentUser, onUpdatePatient, onGenerateInvoice, onCreateLabOrder, onSendPharmacyOrder, labOrders = [], invoices = [], cashTransactions = [], pharmacyStock = [], appConfig = {} }) => {
    const [showConsultationForm, setShowConsultationForm] = useState(false);
    const [showAppointmentForm, setShowAppointmentForm] = useState(false);
    const [newAppointment, setNewAppointment] = useState({
        date: new Date().toISOString().slice(0, 10),
        time: '',
        practitioner: currentUser?.name || 'Praticien',
        motive: '',
        notes: ''
    });

    const practitionerName = currentUser?.name || 'Praticien';
    const initialState = {
        date: new Date().toISOString().slice(0, 10),
        practitioner: practitionerName,
        service: currentUser?.service || 'Consultation',
        signesVitaux: { ta: '', temp: '', fc: '', fr: '', poids: '', taille: '' },
        motifConsultation: ['', '', '', '', ''],
        antecedentsHereditaires: [],
        histoireMaladie: '',
        examenPhysique: '',
        diagnostic: '',
        impressionClinique: ['', '', ''], // up to 3
        examensComplementaires: '',
        conduiteATenir: { laboratoire: '', medicaments: '' },
        notes: '',
        medecinSignature: ''
    };

    const [newConsultation, setNewConsultation] = useState(initialState);
    const lastAISuggestion = useRef({ conduite: '', examens: '' }); // Pour suivre les deux suggestions

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setNewConsultation(prev => ({ ...prev, [name]: value }));
    };

    const handleAppointmentChange = (name, value) => {
        setNewAppointment(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveAppointment = (e) => {
        e.preventDefault();
        const updatedPatient = {
            ...patient,
            appointments: [...(patient.appointments || []), { id: Date.now(), ...newAppointment }]
        };
        onUpdatePatient(updatedPatient);
        setShowAppointmentForm(false);
        setNewAppointment({ date: new Date().toISOString().slice(0, 10), time: '', practitioner: practitionerName, motive: '', notes: '' });
    };

    const handleSignesVitauxChange = (e) => {
        const { name, value } = e.target;
        setNewConsultation(prev => ({ ...prev, signesVitaux: { ...prev.signesVitaux, [name]: value } }));
    };

    const handleMotifChange = (index, value) => {
        setNewConsultation(prev => ({
            ...prev,
            motifConsultation: prev.motifConsultation.map((m, i) => i === index ? value : m)
        }));
    };

    const handleImpressionChange = (index, value) => {
        setNewConsultation(prev => ({
            ...prev,
            impressionClinique: prev.impressionClinique.map((m, i) => i === index ? value : m)
        }));
    };

    const hereditaryOptions = ['Anémie falciforme', 'Diabète', 'Hypertension artérielle', 'Asthme', 'Autre'];
    const toggleHereditary = (value) => {
        setNewConsultation(prev => ({
            ...prev,
            antecedentsHereditaires: prev.antecedentsHereditaires.includes(value)
                ? prev.antecedentsHereditaires.filter(item => item !== value)
                : [...prev.antecedentsHereditaires, value]
        }));
    };

    const handleConduiteChange = (field, value) => {
        setNewConsultation(prev => ({ ...prev, conduiteATenir: { ...prev.conduiteATenir, [field]: value } }));
    };

    // --- LOGIQUE DE L'ASSISTANT IA AMÉLIORÉ ---
    useEffect(() => {
        const generateAndApplySuggestion = () => {
            const impressionText = Array.isArray(newConsultation.impressionClinique)
                ? newConsultation.impressionClinique.join(' ').trim()
                : (newConsultation.impressionClinique || '').toString();

            if (!impressionText) {
                lastAISuggestion.current = { conduite: '', examens: '' };
                return;
            }

            const impression = impressionText.toLowerCase();
            let conduiteSuggestions = [];
            let examensSuggestions = [];

            if (impression.includes('grippe') || impression.includes('rhume')) {
                conduiteSuggestions = ['Paracétamol 1g 3x/jour si fièvre ou douleur', 'Repos', 'Hydratation suffisante'];
                examensSuggestions = ['Test rapide antigénique (TRA) si suspicion de grippe A/B'];
            } else if (impression.includes('gastro')) {
                conduiteSuggestions = ['Antispasmodiques', 'Smectite', 'Soluté de réhydratation orale (SRO)'];
                examensSuggestions = ['Coproparasitologie si symptômes persistants > 7 jours'];
            } else if (impression.includes('entorse')) {
                conduiteSuggestions = ['Reposer le membre affecté', 'Glace 20 min toutes les 2-3h', 'Compression'];
                examensSuggestions = ['Radiographie du membre affecté pour écarter une fracture'];
            } else if (impression.includes('infection urinaire') || impression.includes('brûlures mictionnelles')) {
                conduiteSuggestions = ['Antibiotiques (ex: Fosfomycine)', 'Analgésiques si douleur', 'Boire abondamment'];
                examensSuggestions = ['ECBU (Examen Cyto-Bactériologique des Urines)'];
            } else if (impression.includes('douleur thoracique')) {
                conduiteSuggestions = ['Mise en observation', 'Antalgiques selon EVA', 'Position semi-assise'];
                examensSuggestions = ['ECG (Électrocardiogramme)', 'Marqueurs cardiaques (Troponine)', 'Radiographie thoracique'];
            }

            const newConduiteSuggestion = conduiteSuggestions.join('\n');
            const newExamensSuggestion = examensSuggestions.join('\n');

            setNewConsultation(prev => ({
                ...prev,
                conduiteATenir: {
                    laboratoire: (prev.conduiteATenir.laboratoire === '' || prev.conduiteATenir.laboratoire === lastAISuggestion.current.examens) ? newExamensSuggestion : prev.conduiteATenir.laboratoire,
                    medicaments: (prev.conduiteATenir.medicaments === '' || prev.conduiteATenir.medicaments === lastAISuggestion.current.conduite) ? newConduiteSuggestion : prev.conduiteATenir.medicaments
                },
                examensComplementaires: (prev.examensComplementaires === '' || prev.examensComplementaires === lastAISuggestion.current.examens) ? newExamensSuggestion : prev.examensComplementaires,
            }));

            lastAISuggestion.current = { conduite: newConduiteSuggestion, examens: newExamensSuggestion };
        };

        const handler = setTimeout(generateAndApplySuggestion, 600);
        return () => clearTimeout(handler);

    }, [newConsultation.impressionClinique]);

    const handleShowConsultationForm = () => { setShowConsultationForm(true); setNewConsultation(initialState); lastAISuggestion.current = { conduite: '', examens: '' }; };

    const handleSaveConsultation = (e) => {
        e.preventDefault();
        if (!newConsultation.medecinSignature.trim()) {
            alert('La signature du médecin traitant est obligatoire.');
            return;
        }
        const updatedPatient = { ...patient, consultations: [...(patient.consultations || []), { id: Date.now(), ...newConsultation }] };
        onUpdatePatient(updatedPatient);
        const patientSummary = { patientId: patient.id, patientName: `${patient.prenom || ''} ${patient.name || ''}`.trim() };
        const laboratoryText = `${newConsultation.examensComplementaires}\n${newConsultation.conduiteATenir.laboratoire}`.trim();
        const medicationText = newConsultation.conduiteATenir.medicaments.trim();
        if (laboratoryText && onCreateLabOrder) {
            onCreateLabOrder({ id: createLabOrderCode(), ...patientSummary, tests: laboratoryText.split(/[\n,]+/).map(item => item.trim()).filter(Boolean), createdBy: currentUser?.name || 'Médecin', date: new Date().toISOString().slice(0, 10), status: 'pending', sentTo: ['Laboratoire'], source: 'consultation' });
        }
        if (medicationText && onSendPharmacyOrder) {
            onSendPharmacyOrder({ id: `ORD-${Date.now()}`, ...patientSummary, medications: medicationText, createdBy: currentUser?.name || 'Médecin', date: new Date().toISOString().slice(0, 10), status: 'pending', sentTo: ['Pharmacie'], source: 'consultation' });
        }
        setShowConsultationForm(false);
    };

    const handleExportDossier = () => {
        generatePatientDossierPdf(patient, labOrders, invoices, cashTransactions, pharmacyStock, appConfig);
    };

    return (
        <div>
            {/* ... (Détails patient et bouton retour) ... */}
            <div style={{ marginBottom: '20px' }}>
                <p><strong>Code patient :</strong> {patient.code || '—'}</p>
                {patient.insurance && <p><strong>Assurance :</strong> {patient.insurance.name} · Code {patient.insurance.code} · Adhérent {patient.insurance.memberNumber || '—'}</p>}
                <p><strong>Date d'inscription :</strong> {patient.dateInscription || 'Non renseignée'}</p>
                <button type="button" onClick={handleExportDossier} style={{ marginTop: 12 }}>Exporter le dossier patient (PDF)</button>
            </div>
            <div className="appointments-section" style={{ marginBottom: '24px' }}>
                <h3>Rendez-vous</h3>
                {!showAppointmentForm && <button onClick={() => setShowAppointmentForm(true)}>Ajouter un rendez-vous</button>}
                {showAppointmentForm && (
                    <form onSubmit={handleSaveAppointment} style={{ marginTop: '12px', padding: '16px', border: '1px solid #d8e2ef', borderRadius: '12px' }}>
                        <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
                            <div>
                                <label>Date</label>
                                <input type="date" value={newAppointment.date} onChange={(e) => handleAppointmentChange('date', e.target.value)} style={{ width: '100%' }} required />
                            </div>
                            <div>
                                <label>Heure</label>
                                <input type="time" value={newAppointment.time} onChange={(e) => handleAppointmentChange('time', e.target.value)} style={{ width: '100%' }} required />
                            </div>
                            <div>
                                <label>Praticien</label>
                                <input type="text" value={newAppointment.practitioner} onChange={(e) => handleAppointmentChange('practitioner', e.target.value)} style={{ width: '100%' }} />
                            </div>
                        </div>
                        <div style={{ marginTop: '12px' }}>
                            <label>Motif</label>
                            <input type="text" value={newAppointment.motive} onChange={(e) => handleAppointmentChange('motive', e.target.value)} style={{ width: '100%' }} required />
                        </div>
                        <div style={{ marginTop: '12px' }}>
                            <label>Notes</label>
                            <textarea value={newAppointment.notes} onChange={(e) => handleAppointmentChange('notes', e.target.value)} style={{ width: '100%' }} rows={3} />
                        </div>
                        <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
                            <button type="submit">Enregistrer</button>
                            <button type="button" onClick={() => setShowAppointmentForm(false)}>Annuler</button>
                        </div>
                    </form>
                )}
                {patient.appointments && patient.appointments.length > 0 ? (
                    <ul style={{ marginTop: '12px', padding: '0', listStyle: 'none' }}>
                        {patient.appointments.map(app => (
                            <li key={app.id} style={{ marginBottom: '10px', padding: '12px', border: '1px solid #e1e8f0', borderRadius: '10px' }}>
                                <p><strong>{app.date} {app.time}</strong> - {app.motive}</p>
                                <p>Praticien: {app.practitioner}</p>
                                {app.notes && <p>Notes: {app.notes}</p>}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p style={{ marginTop: '12px' }}>Aucun rendez-vous planifié.</p>
                )}
            </div>
            <div className="consultations-section">
                <h3>Dossier de Consultations</h3>
                {!showConsultationForm && <button onClick={handleShowConsultationForm}>Ajouter une consultation</button>}

                {showConsultationForm && (
                    <form onSubmit={handleSaveConsultation}>
                        <fieldset>
                            <legend>Signes Vitaux</legend>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '12px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}><label>TA</label><input name="ta" type="text" placeholder="12/8" value={newConsultation.signesVitaux.ta} onChange={handleSignesVitauxChange} /></div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}><label>T°</label><input name="temp" type="text" placeholder="37.2" value={newConsultation.signesVitaux.temp} onChange={handleSignesVitauxChange} /></div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}><label>FC</label><input name="fc" type="text" placeholder="78" value={newConsultation.signesVitaux.fc} onChange={handleSignesVitauxChange} /></div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}><label>FR</label><input name="fr" type="text" placeholder="18" value={newConsultation.signesVitaux.fr} onChange={handleSignesVitauxChange} /></div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}><label>Poids</label><input name="poids" type="text" placeholder="kg" value={newConsultation.signesVitaux.poids} onChange={handleSignesVitauxChange} /></div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}><label>Taille</label><input name="taille" type="text" placeholder="cm" value={newConsultation.signesVitaux.taille} onChange={handleSignesVitauxChange} /></div>
                            </div>
                        </fieldset>
                        <fieldset>
                            <legend>Motif de Consultation (max 5)</legend>
                            {[0, 1, 2, 3, 4].map(i => (
                                <div key={i}>
                                    <label>{i + 1}) </label>
                                    <input type="text" value={newConsultation.motifConsultation[i]} onChange={(e) => handleMotifChange(i, e.target.value)} placeholder={`Motif ${i + 1}`} style={{ width: '100%' }} />
                                </div>
                            ))}
                        </fieldset>
                        <fieldset>
                            <legend>Maladies héréditaires</legend>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px' }}>
                                {hereditaryOptions.map(option => <label key={option}><input type="checkbox" checked={newConsultation.antecedentsHereditaires.includes(option)} onChange={() => toggleHereditary(option)} /> {option}</label>)}
                            </div>
                        </fieldset>
                        <fieldset>
                            <legend>Histoire de la maladie</legend>
                            <textarea name="histoireMaladie" value={newConsultation.histoireMaladie} onChange={handleFormChange} placeholder="Racontez l'évolution des symptômes" style={{ width: '100%' }} />
                        </fieldset>

                        <fieldset>
                            <legend>Examen physique</legend>
                            <textarea name="examenPhysique" value={newConsultation.examenPhysique} onChange={handleFormChange} placeholder="Troubles, signes cliniques trouvés à l'examen" style={{ width: '100%' }} />
                        </fieldset>

                        <fieldset>
                            <legend>Diagnostic médical (Dx)</legend>
                            <textarea name="diagnostic" value={newConsultation.diagnostic} onChange={handleFormChange} placeholder="Diagnostic principal" style={{ width: '100%' }} />
                        </fieldset>

                        <fieldset>
                            <legend>Impression clinique (max 3)</legend>
                            {[0, 1, 2].map(i => (
                                <div key={i} style={{ marginBottom: 6 }}>
                                    <label>{`Impression ${i + 1}`}</label>
                                    <input type="text" value={newConsultation.impressionClinique[i] || ''} onChange={(e) => handleImpressionChange(i, e.target.value)} placeholder={`Impression ${i + 1}`} style={{ width: '100%' }} />
                                </div>
                            ))}
                        </fieldset>

                        <fieldset>
                            <legend>Examens complémentaires & Laboratoire</legend>
                            <textarea name="examensComplementaires" value={newConsultation.examensComplementaires} onChange={handleFormChange} placeholder="Examens complémentaires suggérés (ex: ECBU, NFS...)" style={{ width: '100%' }} />
                        </fieldset>

                        <fieldset>
                            <legend>Conduite à tenir</legend>
                            <div style={{ marginBottom: 8 }}>
                                <label>Examens/Laboratoire</label>
                                <textarea value={newConsultation.conduiteATenir.laboratoire} onChange={(e) => handleConduiteChange('laboratoire', e.target.value)} placeholder="Examens à prescrire" style={{ width: '100%' }} />
                            </div>
                            <div style={{ marginBottom: 8 }}>
                                <label>Médicaments</label>
                                <textarea value={newConsultation.conduiteATenir.medicaments} onChange={(e) => handleConduiteChange('medicaments', e.target.value)} placeholder="Ordonnance / médicaments recommandés" style={{ width: '100%' }} />
                            </div>
                            <div style={{ marginBottom: 8 }}>
                                <label>Notes et conseils du médecin</label>
                                <textarea value={newConsultation.notes} onChange={(e) => handleFormChange({ target: { name: 'notes', value: e.target.value } })} placeholder="Conseils, observations ou informations complémentaires" style={{ width: '100%' }} rows={3} />
                            </div>
                            <div>
                                <label>Signature obligatoire du médecin traitant</label>
                                <input name="medecinSignature" value={newConsultation.medecinSignature} onChange={handleFormChange} placeholder="Nom du médecin" style={{ width: '100%' }} />
                            </div>
                        </fieldset>

                        <div style={{ marginTop: 10 }}>
                            <button type="submit">Enregistrer</button>
                            <button type="button" onClick={() => setShowConsultationForm(false)} style={{ marginLeft: 8 }}>Annuler</button>
                        </div>
                    </form>
                )}

                <ul className="consultation-list">
                    {(patient.consultations || []).sort((a, b) => new Date(b.date) - new Date(a.date)).map(consult => (
                        <li key={consult.id} className="consultation-item">
                            <p><strong>Date:</strong> {consult.date} | <strong>Praticien:</strong> {consult.practitioner}</p>
                            {consult.signesVitaux && <p><strong>Signes vitaux:</strong> TA: {consult.signesVitaux.ta}, T°: {consult.signesVitaux.temp}, FC: {consult.signesVitaux.fc}, FR: {consult.signesVitaux.fr}, Poids: {consult.signesVitaux.poids}, Taille: {consult.signesVitaux.taille}</p>}
                            {consult.motifConsultation && consult.motifConsultation.filter(m => m).length > 0 && <p><strong>Motif(s):</strong> {consult.motifConsultation.map((m, i) => m ? `${i + 1}) ${m}` : null).filter(m => m).join(' | ')}</p>}
                            {consult.histoireMaladie && <p><strong>Histoire de la maladie:</strong> <pre>{consult.histoireMaladie}</pre></p>}
                            {consult.examenPhysique && <p><strong>Examen physique:</strong> <pre>{consult.examenPhysique}</pre></p>}
                            {consult.diagnostic && <p><strong>Diagnostic (Dx):</strong> {consult.diagnostic}</p>}
                            {consult.impressionClinique && Array.isArray(consult.impressionClinique) && consult.impressionClinique.filter(i => i).length > 0 && (
                                <p><strong>Impression(s) clinique(s):</strong> {consult.impressionClinique.filter(i => i).join(' | ')}</p>
                            )}
                            {consult.examensComplementaires && <p><strong>Examens Complémentaires:</strong> <pre>{consult.examensComplementaires}</pre></p>}
                            {consult.conduiteATenir && (
                                <div>
                                    <p><strong>Conduite - Laboratoire:</strong></p>
                                    <pre>{consult.conduiteATenir.laboratoire || '—'}</pre>
                                    <p><strong>Conduite - Médicaments:</strong></p>
                                    <pre>{consult.conduiteATenir.medicaments || '—'}</pre>
                                </div>
                            )}
                            {consult.medecinSignature && <p><strong>Signature médecin:</strong> {consult.medecinSignature}</p>}
                            {consult.antecedentsHereditaires?.length > 0 && <p><strong>Antécédents héréditaires:</strong> {consult.antecedentsHereditaires.join(', ')}</p>}
                            {consult.notes && <p><strong>Notes:</strong> {consult.notes}</p>}
                            <div className="consultation-actions">
                                <button onClick={() => onGenerateInvoice(consult)}>Générer une facture</button>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

export default PatientDetails;
